import Icons from "@/assets/icons";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ScrollArea } from "@radix-ui/react-scroll-area";
import { SetStateAction, useEffect, useRef, useState } from "react";
import { messageService } from "@/services/MessageService";
import { t } from "i18next";
import { ApiService } from "@/services/ApiService";

type request = {
  method: string;
  url: string;
  body?: any;
};

interface LogDialogProps {
  open: boolean;
  setOpen: React.Dispatch<SetStateAction<boolean>>;
  request: request;
  isPolling: boolean;
  setRequest: React.Dispatch<React.SetStateAction<request | null>>;
}

const LogDialog: React.FC<LogDialogProps> = ({
  open,
  setOpen,
  request,
  setRequest,
  isPolling,
}) => {
  const [apiData, setApiData] = useState<string[]>([]);
  const scrollRef = useRef<HTMLDivElement>(null);
  const [error, setError] = useState(false);
  const { post, get } = ApiService;

  // Responsável por persistir o scroll do usuário no fim do ScrollArea
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    const isAtBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 50;

    if (isAtBottom) {
      el.scrollTop = el.scrollHeight;
    }
  }, [apiData]);

  // Responsável por buscar os logs da transação
  useEffect(() => {
    let interval: NodeJS.Timeout | null = null;

    const fetchData = async (req: request) => {
      try {
        const promisse =
          req.method === "POST" ? post(req.url, req.body) : get(req.url);

        const { data } = await promisse;
        setApiData(data);
        setError(false);
      } catch (e: any) {
        setError(true);
        const message: string = e.data?.detail ?? e;
        setOpen(false);

        message.startsWith("CONFIRM:")
          ? messageService.simNaoConfirm(
              "Atenção",
              message.split(":")[1],
              () => {
                const newRequest = {
                  method: req.method,
                  url: req.url,
                  body: { ...req?.body, continuarExc: true },
                };

                setRequest(newRequest);
                setOpen(true);
              }
            )
          : messageService.errorWithDetail("Erro ao buscar logs", message);
      }
    };

    if (request) {
      fetchData(request);

      if (!error && isPolling) {
        interval = setInterval(() => fetchData(request), 1000);
      }
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [error, isPolling, request]);

  const scrollAreaStyle =
    "p-2 border rounded h-120 overflow-auto [&::-webkit-scrollbar]:h-2 [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-thumb]:bg-gray-300 [&::-webkit-scrollbar-track:bg-gray-100 [&::-webkit-scrollbar-thumb:horizontal]:bg-gray-300 [&::-webkit-scrollbar-track:horizontal]:bg-gray-100";

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="!max-w-5xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Icons.ListAlt /> {t("logDialog.title")}
          </DialogTitle>
        </DialogHeader>
        <ScrollArea ref={scrollRef} className={scrollAreaStyle}>
          {apiData.length ? (
            <p className="whitespace-pre-line font-mono space-y-1.5 font-medium text-sm max-w-5xl">
              {apiData.join("\n")}
            </p>
          ) : (
            <p className="text-center font-semibold">
              {t("logDialog.loading")}
            </p>
          )}
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};

export default LogDialog;
